// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Chip, RemoteTable, Tabs } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import UploadHelpButton from "components/UploadHelpButton";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectTimeSelector } from "state/application/applicationSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";
import {
  useAddEVChargingIntervalsForecastMutation,
  useGetEVChargingIntervalsForecastByCommunityIdPaginationQuery,
} from "state/evs/evApiSlice";

// Utils
import { handleFileUpload } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";
import { datetimeToDate, datetimeToTime } from "utils/time";
import { useGetResources } from "utils/crud/read";

import SwimlaneChart from "./SwimlaneChart";

const HREF_TEMPLATE = "/docs/template_charging_intervals_forecast.csv";
const TASK_TYPE = "EV_CHARGING_INTERVAL_FORECAST";

/* Server-paginated rec table of forecast intervals (date / time / EV / state). */
function ForecastTable({ communityId }) {
  const { t } = useTranslation(["internalLoads", "common"]);
  const { startDate, endDate } = useSelector(selectTimeSelector);
  const resources = useGetResources("true");
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);

  useEffect(() => {
    setPage(0);
  }, [startDate, endDate]);

  const { data, isFetching } = useGetEVChargingIntervalsForecastByCommunityIdPaginationQuery(
    {
      page,
      limit,
      date: startDate === endDate ? startDate : null,
      startDate: startDate !== endDate ? startDate : null,
      endDate: startDate !== endDate ? endDate : null,
      communityId,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const evName = (evId) => resources?.find((r) => r.id === evId)?.name ?? String(evId).slice(0, 8);

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: `${row.ev_id}-${row.datetime_timestamp}-${i}`,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        ev: evName(row.ev_id),
        connected: row.is_connected === true || row.is_connected === "True",
      })),
    [data, resources]
  );

  const columns = [
    { key: "date", label: t("table.columns.date"), width: "20%" },
    { key: "time", label: t("table.columns.time"), width: "18%" },
    {
      key: "ev",
      label: t("pages.ev_charging_intervals_forecast.table.columns.ev"),
      render: (v) => <span className="font-medium">{v}</span>,
    },
    {
      key: "connected",
      label: t("pages.ev_charging_intervals_forecast.table.columns.is_connected", {
        defaultValue: "Conectado",
      }),
      render: (v) =>
        v ? (
          <Chip tone="green">
            <Icon name="ev_station" style={{ fontSize: 13 }} />
            {t("pages.ev_charging_intervals_forecast.legend_connected", {
              defaultValue: "Conectado",
            })}
          </Chip>
        ) : (
          <Chip tone="gray">
            {t("pages.ev_charging_intervals_forecast.legend_disconnected", {
              defaultValue: "Desconectado",
            })}
          </Chip>
        ),
    },
  ];

  return (
    <RemoteTable
      columns={columns}
      rows={rows}
      totalCount={data?.total_count ?? 0}
      page={page}
      limit={limit}
      onPageChange={setPage}
      sort={sortOrder}
      onSortChange={setSortOrder}
      loading={isFetching}
    />
  );
}

ForecastTable.propTypes = {
  communityId: PropTypes.string.isRequired,
};

function ManageEVChargingIntervalsForecast({ noWrapper }) {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const activeTasks = useSelector(selectActiveTasksByType(TASK_TYPE));
  const [tab, setTab] = useState("table");
  const { t } = useTranslation(["internalLoads", "erse"]);

  // CREATE (csv upload through the async task pipeline)
  const [addForecast] = useAddEVChargingIntervalsForecastMutation();
  const onUpload = handleFileUpload(addForecast, community.id, dispatch, TASK_TYPE);

  // Task status polling while an upload task is active
  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeTasks.length === 0,
    }
  );
  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: activeTasks,
    dispatch,
    TASK_TYPE,
  });

  const isUploading = activeTasks.length > 0;
  const uploadId = `loads-upload-${TASK_TYPE}`;

  const actions = (
    <div className="flex items-center gap-[10px] flex-wrap">
      {isUploading && (
        <Chip tone="amber">
          <Icon name="hourglass_top" style={{ fontSize: 13 }} />
          {t("uploading", { defaultValue: "A carregar…" })}
        </Chip>
      )}
      <a className="rec-btn-ghost rec-btn-sm no-underline" href={HREF_TEMPLATE}>
        <Icon name="download" style={{ fontSize: 16 }} />
        {t("list.buttons.template", { ns: "erse", defaultValue: "Template" })}
      </a>
      <UploadHelpButton />
      <label
        htmlFor={uploadId}
        className="rec-btn-ghost rec-btn-sm"
        style={isUploading ? { opacity: 0.4, pointerEvents: "none" } : undefined}
      >
        <Icon name="upload_file" style={{ fontSize: 16 }} />
        {t("upload", { defaultValue: "Carregar dados" })}
        <input id={uploadId} type="file" accept=".csv" hidden onChange={onUpload} />
      </label>
    </div>
  );

  const TABS = [
    { id: "table", label: t("tabs.table"), icon: "table_rows" },
    { id: "charts", label: t("tabs.charts"), icon: "insights" },
  ];

  const content = (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="rec-section-title !mb-0">
            <Icon name="ev_station" />
            {t("pages.ev_charging_intervals_forecast.title")}
          </h3>
          <p className="text-[12.5px] text-ink-3 m-0 mt-1">
            {t("pages.ev_charging_intervals_forecast.subtitle")}
          </p>
        </div>
        {actions}
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "table" ? (
        <div className="rec-card rec-card-pad">
          <ForecastTable communityId={community.id} />
        </div>
      ) : (
        <SwimlaneChart communityId={community.id} />
      )}
    </div>
  );

  return noWrapper ? content : <div className="rec-page">{content}</div>;
}

ManageEVChargingIntervalsForecast.defaultProps = {
  noWrapper: false,
};

ManageEVChargingIntervalsForecast.propTypes = {
  noWrapper: PropTypes.bool,
};

export default ManageEVChargingIntervalsForecast;
